import { InputLabelProps } from "@src/components/elements/TextInput";
import React, { ChangeEventHandler, ForwardedRef, ReactElement } from "react";
import styled from "styled-components/macro";

type Props = {
  name: string;
  value?: string | null;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  error?: string;
  disabled?: boolean;
  hideError?: boolean;
} & InputLabelProps;

const StyledRoot = styled.div<{ labelType?: Props["labelType"] }>`
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  label {
    font-family: var(--inter-font);
    font-size: var(--font-size-small);
  }

  .error {
    color: var(--error-color);
    font-family: var(--inter-font);
    font-size: var(--font-size-small);
  }

  .input-container {
    position: relative;
    width: 100%;
    .floating-label-input {
      &::placeholder {
        color: var(--border-gray);
      }
      &:focus::placeholder {
        color: transparent;
      }
    }

    .floating-label {
      position: absolute;
      transition: all 0.3s ease;
      user-select: none;
      pointer-events: none;
    }

    textarea:not(:focus) + .floating-label {
      top: 1rem;
      left: 0.75rem;
      transform: translateY(-50%);
      color: transparent;
    }

    textarea:focus + .floating-label,
    textarea:not(:placeholder-shown) + .floating-label {
      background-color: var(--page-background);
      top: 0px;
      left: 0.25rem;
      transform: translateY(-50%);
      padding: 0 0.25rem;
      color: var(--secondary-black);

      #required {
        color: transparent;
      }
    }
  }
`;

const StyledTextarea = styled.textarea<{
  error?: Props["error"];
  labelType?: Props["labelType"];
}>`
  border: 1px solid
    ${(props) => (props.error ? "var(--error-color)" : "var(--border-gray)")};
  border-radius: var(--rounded-corner);
  padding: 0.625rem 0.75rem;
  margin-top: ${(props) =>
    props.labelType === "floating" ? "0.5rem" : undefined};
  font-family: var(--inter-font);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-small);
  transition: var(--transition);
  height: 5rem;
  width: 100%;

  &:hover {
    border-color: var(--dark-gray);
    box-shadow: var(--drop-shadow-light);
  }
  &:focus {
    outline: none;
    border-color: var(--green);
    box-shadow: var(--drop-shadow-light);
  }

  &::placeholder {
    color: var(--border-gray);
  }
`;

export const Textarea = React.forwardRef(
  (
    {
      name,
      label,
      labelType = "normal",
      value,
      placeholder,
      onChange,
      required,
      defaultValue,
      className,
      error,
      disabled,
      hideError,
    }: Props,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ): ReactElement => {
    return (
      <StyledRoot
        labelType={labelType}
        className={`${error ? "field-error" : ""} ${className ?? ""}`}
      >
        {label && labelType === "normal" && (
          <label htmlFor={label}>
            {label}
            {required && <span className="required">*</span>}
          </label>
        )}
        <div className="row input-container">
          <StyledTextarea
            draggable={false}
            disabled={disabled}
            error={error}
            ref={ref}
            id={name}
            value={value ?? undefined}
            defaultValue={defaultValue}
            placeholder={labelType === "floating" ? label : placeholder}
            onChange={onChange}
            required={required}
            className={labelType === "floating" ? "floating-label-input" : ""}
          />
          {labelType === "floating" ? (
            <label htmlFor={name} className="floating-label label">
              {label}
              {required && <span className="required">*</span>}
            </label>
          ) : undefined}
        </div>
        {!hideError ? <div className="error">{error}&nbsp;</div> : null}
      </StyledRoot>
    );
  },
);

Textarea.displayName = "Textarea";
